import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import _ from 'lodash';
import dayjs from 'dayjs';
import Yup from '../../../utils/yup.js';
import utc from 'dayjs/plugin/utc';

import addNotification from '../../../utils/addNotification.jsx';
import { NotificationType } from '../../../config/index.js';
import useMenus from '../../../context/MenuContext.js';
import useUsers from '../../../context/UsersContext.js';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI, FormBase } from '../../Interface/index.js';
import { TaskListTemplateFields } from './TaskListTemplateFields.jsx';
import TaskListTemplateItem from './TaskListTemplateItem.jsx';
import TaskListTemplateReports from './TaskListTemplateReports.jsx';
import taskListService from '../../../service/taskList.service.js';
import masterDataService from '../../../service/masterData.service.js';
import { generateAutocompleteItems, generateSubItems } from '../../../utils/helper.js';
import { Dependence } from '../../../config/taskList.js';
import useParam from '../../../context/ParamContext.js';
import { getResponsibleList } from '../../../utils/getResponsibleList';

export const TaskListTemplatePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { params, getParam } = useParam();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    projectType: [],
    responsible: [],
  });

  const defaultTaskListItem = {
    id: null,
    isNew: true,
    name: '',
    responsiblePersonId: null,
    duration: null,
    dependence: Dependence.FS,
    createdBy: user.userId,
    visOrder: null,
    userGroupId: null,
    construction: true,
    waitingTime: null,
    userId: user.userId,
    taskRatio: null,
  };

  const def = {
    name: '',
    projectTypeId: null,
    taskListTemplateItem: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('taskListTemplate');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    promiseArray.push(getResponsibleList());

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          userGroups: resp[1],
          projectType: resp[2],
          responsible: resp[3],
        };

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroups: generateAutocompleteItems(aData.userGroups, 'name', 'id'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          responsible: aData.responsible,
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    projectTypeId: Yup.string().nullable(),
  });

  const valuesToForm = () => {
    const { name, projectTypeId, taskListTemplateItem } = selectedValues;

    setInitValues({
      name,
      projectTypeId,
      taskListTemplateItem: taskListTemplateItem.map((item) => ({
        id: item.id,
        name: item.name,
        responsiblePersonId: item.responsiblePersonId,
        dependence: item.dependence,
        duration: item.duration,
        createdBy: user.userId,
        visOrder: item.visOrder,
        path: item.path,
        parentId: item.parentId,
        userGroupId: item.userGroupId,
        construction: item.construction,
        waitingTime: item.waitingTime,
        userId: item.userId,
        taskRatio: item.taskRatio,
        responsibleType: item.responsibleType,
        taskListRespPerson: item?.taskListRespPerson?.map((respPerson) => ({
          id: respPerson.id,
          userId: respPerson.userId,
        })),
      })),
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const generatedValues = generateSubItems(values.taskListTemplateItem);

    const updData = {
      ...values,
      taskListTemplateItem: generatedValues,
      updatedBy: user.userId,
    };

    taskListService.updateTaskListTemplate(updData, selectedValues.id).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const generatedValues = generateSubItems(values.taskListTemplateItem);

    const insertObject = {
      ...values,
      taskListTemplateItem: generatedValues,
      createdBy: user.userId,
    };

    taskListService
      .createTaskListTemplate(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    taskListService.deleteTaskListTemplate(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="TaskListTemplate"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(def);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, resetForm, setFieldValue, validateField }) => (
                  <Form>
                    <TaskListTemplateFields
                      viewField={viewField}
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      validateField={validateField}
                      autocompleteOptions={autoCompleteOptions}
                    />
                    <TaskListTemplateItem
                      user={user}
                      disabled={viewField}
                      defaultField={defaultTaskListItem}
                      autoCompleteOptions={autoCompleteOptions}
                    />
                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <TaskListTemplateReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
